import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { useLang } from "@/i18n";
import { LangToggle } from "@/components/Toggles";
import { stageMeta, stageLabel } from "@/lib/constants";
import { Droplets, Boxes, Scale, Clock, Loader2 } from "lucide-react";

export default function LiveQueue() {
  const { slug } = useParams();
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [now, setNow] = useState(new Date());

  const load = useCallback(() => {
    api.get(`/public/live-queue/${slug}`).then((r) => setData(r.data)).catch(() => setError(true));
  }, [slug]);

  useEffect(() => {
    load();
    const poll = setInterval(load, 8000);
    const clock = setInterval(() => setNow(new Date()), 1000);
    return () => { clearInterval(poll); clearInterval(clock); };
  }, [load]);

  if (error) return <Centered>{t("order_not_found")}</Centered>;
  if (!data) return <Centered><Loader2 className="h-8 w-8 animate-spin text-amber" /></Centered>;

  const stages = data.stages || [];
  const firstStage = stages[0]?.id;
  const idxOf = (status) => stages.findIndex((s) => s.id === status);
  const processing = data.orders.filter((o) => o.status !== firstStage);
  const nowServing = processing[0] || data.orders[0];
  const upNext = data.orders.filter((o) => o.id !== nowServing?.id).slice(0, 8);

  return (
    <div className="min-h-screen bg-[#121811] text-[#F4F7F2]">
      <header className="flex items-center justify-between border-b border-white/10 px-6 py-5 lg:px-12">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center overflow-hidden rounded-xl bg-amber text-olive">
            {data.logo ? <img src={data.logo} alt="" data-testid="live-logo" className="h-full w-full object-contain" /> : <Droplets className="h-6 w-6" />}
          </div>
          <div>
            <h1 className="font-heading text-xl font-bold lg:text-2xl">{data.mill_name}</h1>
            <p className="text-sm text-white/50">{t("live_title")}</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className="font-mono text-2xl font-bold lg:text-3xl">{now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</p>
            <p className="flex items-center justify-end gap-1 text-xs text-white/50"><span className="h-2 w-2 animate-pulse-soft rounded-full bg-green-400" /> {t("updated")}</p>
          </div>
          <LangToggle />
        </div>
      </header>

      <div className="grid gap-8 p-6 lg:grid-cols-3 lg:p-12">
        {/* Now processing */}
        <div className="lg:col-span-2">
          <p className="mb-4 font-mono text-sm uppercase tracking-widest text-amber">{t("now_processing")}</p>
          {nowServing ? (
            <div className="rounded-3xl border border-amber/30 bg-gradient-to-br from-[#1B2319] to-[#232D21] p-8 lg:p-12" data-testid="now-serving">
              <div className="flex items-center gap-3">
                {(() => { const i = idxOf(nowServing.status); const M = stageMeta(nowServing.status, i); const Icon = M.icon; return (
                  <span className="flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-semibold" style={{ background: `${M.color}22`, color: M.color }}>
                    <Icon className="h-4 w-4" /> {stageLabel(t, stages[i] || { id: nowServing.status })}
                  </span>
                ); })()}
              </div>
              <h2 className="mt-6 font-heading text-5xl font-extrabold lg:text-7xl">{nowServing.client_name}</h2>
              <p className="mt-3 font-mono text-xl text-amber">{nowServing.order_code}</p>
              <div className="mt-8 flex flex-wrap gap-8">
                <Metric icon={Boxes} label={t("crates")} value={nowServing.crate_count} />
                <Metric icon={Scale} label={t("total_weight")} value={`${nowServing.total_weight} kg`} />
                {nowServing.show_yield && <Metric icon={Droplets} label={t("est_yield")} value={`${nowServing.estimated_yield} L`} />}
              </div>
              {nowServing.crates?.length > 0 && (
                <div className="mt-8" data-testid="now-serving-crates">
                  <p className="mb-3 font-mono text-xs uppercase tracking-widest text-white/50">{t("crates")}</p>
                  <div className="flex flex-wrap gap-2">
                    {nowServing.crates.map((c) => (
                      <span key={c.number} data-testid={`crate-chip-${c.number}`} className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2 font-mono text-sm">
                        <span className="font-bold text-amber">#{c.number}</span>
                        <span className="text-white/60">{c.weight} kg</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="rounded-3xl border border-white/10 bg-[#1B2319] p-12 text-center text-white/40">{t("waiting")}…</div>
          )}
        </div>

        {/* Up next */}
        <div>
          <div className="mb-4 flex items-center justify-between">
            <p className="font-mono text-sm uppercase tracking-widest text-white/50">{t("up_next")}</p>
            <span className="rounded-full bg-white/10 px-3 py-1 text-sm">{data.orders.length} {t("in_queue")}</span>
          </div>
          <div className="space-y-3">
            {upNext.length === 0 && <p className="text-white/30">{t("waiting")}…</p>}
            {upNext.map((o, i) => {
              const M = stageMeta(o.status, idxOf(o.status));
              return (
                <div key={o.id} data-testid={`upnext-${o.order_code}`} className="flex items-center gap-4 rounded-2xl border border-white/10 bg-[#1B2319] p-4">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-white/5 font-heading text-lg font-bold text-white/60">{i + 2}</span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-semibold">{o.client_name}</p>
                    <p className="font-mono text-xs text-white/40">{o.order_code} · {o.total_weight} kg</p>
                  </div>
                  <span className="h-2.5 w-2.5 rounded-full" style={{ background: M.color }} />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function Metric({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3">
      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/5"><Icon className="h-6 w-6 text-amber" /></div>
      <div>
        <p className="font-heading text-2xl font-bold lg:text-3xl">{value}</p>
        <p className="text-xs uppercase tracking-wider text-white/40">{label}</p>
      </div>
    </div>
  );
}

function Centered({ children }) {
  return <div className="flex min-h-screen items-center justify-center bg-[#121811] text-white/60">{children}</div>;
}
